/**
 * Edit Metadata Modal
 *
 * Shared modal to edit user-facing metadata (label + free-text notes) on an
 * analysis job or a report. Geometry/analysis results are never editable —
 * only these annotation fields.
 */

import { X, Save, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface EditMetadataModalProps {
    title: string;
    subtitle?: string;
    initialLabel?: string;
    initialNotes?: string;
    /** Persist the changes. Should throw on failure so the modal stays open. */
    onSave: (data: { label: string; notes: string }) => Promise<void>;
    onClose: () => void;
}

export default function EditMetadataModal({
    title,
    subtitle,
    initialLabel,
    initialNotes,
    onSave,
    onClose,
}: EditMetadataModalProps) {
    const { t } = useTranslation();
    const [saving, setSaving] = useState(false);
    const [label, setLabel] = useState(initialLabel || '');
    const [notes, setNotes] = useState(initialNotes || '');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setSaving(true);
            await onSave({ label: label.trim(), notes: notes.trim() });
            onClose();
        } catch {
            // Error toast is surfaced by the caller; keep the modal open.
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4 bg-black/50 animate-fade-in">
            <div className="card p-0 w-full max-w-full sm:max-w-lg animate-slide-up">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="min-w-0">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h2>
                        {subtitle && (
                            <p className="text-sm text-gray-500 truncate">{subtitle}</p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        disabled={saving}
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    <div>
                        <label
                            htmlFor="metadata-label"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            {t('manage.label')}
                        </label>
                        <input
                            type="text"
                            id="metadata-label"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            maxLength={255}
                            className="input w-full"
                            placeholder={t('manage.labelPlaceholder')}
                            disabled={saving}
                            autoFocus
                        />
                    </div>

                    <div>
                        <label
                            htmlFor="metadata-notes"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            {t('manage.notes')}
                        </label>
                        <textarea
                            id="metadata-notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            maxLength={5000}
                            rows={4}
                            className="input w-full resize-y"
                            placeholder={t('manage.notesPlaceholder')}
                            disabled={saving}
                        />
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn-secondary"
                            disabled={saving}
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="submit"
                            className="btn-primary flex items-center gap-2"
                            disabled={saving}
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    {t('common.loading')}
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    {t('common.save')}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
